import React, { useEffect } from 'react';

interface LinkInterceptorProps {
  children: React.ReactNode;
  globalRedirects?: {
    [externalUrl: string]: string;
  };
  redirectRules?: Array<{
    pattern: string | RegExp;
    target: string;
    exact?: boolean;
  }>;
}

/**
 * LinkInterceptor component that automatically intercepts all <a> tags in its children
 * and applies redirect rules to them. This is useful for applying global redirects
 * without having to manually replace every <a> tag with SmartLink.
 * 
 * Usage:
 * <LinkInterceptor 
 *   globalRedirects={{
 *     "https://yourapp.com/profile/123": "/home/user/123"
 *   }}
 *   redirectRules={[
 *     { pattern: /^https:\/\/yourapp\.com\/profile\/(.+)$/, target: '/home/user/$1' }
 *   ]}
 * >
 *   <YourAppContent />
 * </LinkInterceptor>
 */
export const LinkInterceptor: React.FC<LinkInterceptorProps> = ({ 
  children, 
  globalRedirects = {},
  redirectRules = []
}) => {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (!link || !link.href) return;

      const href = link.href;

      // Check exact matches first
      if (globalRedirects[href]) {
        e.preventDefault();
        window.location.href = globalRedirects[href];
        return;
      }

      // Check pattern-based rules
      for (const rule of redirectRules) {
        if (rule.exact) {
          if (rule.pattern === href) {
            e.preventDefault();
            window.location.href = rule.target;
            return;
          }
        } else {
          const regex = rule.pattern instanceof RegExp ? rule.pattern : new RegExp(rule.pattern);
          const match = href.match(regex);
          if (match) {
            e.preventDefault();
            // Replace $1, $2, etc. with captured groups
            const targetRoute = rule.target.replace(/\$(\d+)/g, (_, groupIndex) => {
              return match[parseInt(groupIndex)] || '';
            });
            window.location.href = targetRoute;
            return;
          }
        }
      }
    };

    // Add event listener to the document
    document.addEventListener('click', handleClick, true);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [globalRedirects, redirectRules]);

  return <>{children}</>;
};

export default LinkInterceptor;
